import type { RowLabel } from '../../domain/rowLabel.js';
import { isActiveNav } from '../AppLayout/navSections.js';

/**
 * One step of a page's trail. `to` makes it a link; `mono` marks a record key; `secondary` is a
 * named record's id, shown after the name in monospace (D4 inline label).
 */
export type Crumb = { label: string; to?: string; mono?: boolean; secondary?: string | null };

/** A crumb read as the `RowLabel` it already carries the shape of, for `RowLabelInline`. */
export const crumbLabel = (crumb: Crumb): RowLabel => ({
    label: crumb.label,
    secondary: crumb.secondary ?? null,
    labelIsId: crumb.mono ?? false,
});

/** A record's own crumb: `label`, or `fallback` before it has one (a create). `to` makes it a link. */
export const crumbOf = (label: RowLabel | null, fallback: string, to?: string): Crumb => ({
    label: label?.label ?? fallback,
    to,
    mono: label?.labelIsId ?? false,
    secondary: label?.secondary ?? null,
});

type SectionedLink = { to: string; section: string };

/** The section of the nav link a path sits under: the longest link that is the path or a prefix of it. */
export const sectionFor = (pathname: string, links: readonly SectionedLink[]): string | null =>
    links.filter((link) => isActiveNav(link.to, pathname)).sort((a, b) => b.to.length - a.to.length)[0]?.section ??
    null;

/** The header's trail: the nav section, then the page's own crumbs — or its title when it has none. */
export const headerCrumbs = (
    pathname: string,
    links: readonly SectionedLink[],
    title: string | undefined,
    breadcrumbs: readonly Crumb[] | undefined,
): Crumb[] => {
    const own: Crumb[] = breadcrumbs && breadcrumbs.length > 0 ? [...breadcrumbs] : title ? [{ label: title }] : [];
    if (own.length === 0) return [];
    const section = sectionFor(pathname, links);
    return section && own[0]?.label !== section ? [{ label: section }, ...own] : own;
};
